(() => {
  'use strict';

  const SELECTORS = {
    reveal: '[data-zv-reveal]',
    flow: '[data-zv-flow]',
    day: '[data-zv-daycycle]',
    modular: '[data-zv-modular]',
    chargeRing: '[data-zv-charge-ring]',
    appDevice: '[data-zv-app-device]',
    countdown: '[data-zv-countdown]',
    counters: '[data-zv-counter]',
    reviews: '[data-zv-reviews-slider]',
    blogSliders: '[data-zv-blog-slider]'
  };

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function initReveals(scope = document) {
    const items = [...scope.querySelectorAll(SELECTORS.reveal)].filter((item) => !item.dataset.zvRevealReady);

    if (!items.length) return;

    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      items.forEach((item) => {
        item.dataset.zvRevealReady = 'true';
        item.classList.add('is-visible');
      });
      return;
    }

    const observer = new IntersectionObserver((entries, activeObserver) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        activeObserver.unobserve(entry.target);
      });
    }, {
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.12
    });

    items.forEach((item) => {
      item.dataset.zvRevealReady = 'true';
      observer.observe(item);
    });
  }

  function initFlow(root) {
    if (!root || root.dataset.zvReady) return;
    root.dataset.zvReady = 'true';

    const stage = root.querySelector('[data-zv-flow-stage]');
    const tabs = [...root.querySelectorAll('[data-zv-flow-tab]')];
    const mobilePanels = [...root.querySelectorAll('[data-zv-flow-mobile-panel]')];
    const caption = root.querySelector('[data-mode-caption]');
    let currentIndex = Math.max(0, tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true'));

    const setMode = (index, focus = false) => {
      if (!tabs.length) return;
      currentIndex = (index + tabs.length) % tabs.length;
      const activeTab = tabs[currentIndex];
      const mode = activeTab.dataset.zvFlowTab;

      tabs.forEach((tab, tabIndex) => {
        const active = tabIndex === currentIndex;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
      });

      if (stage) stage.dataset.mode = mode;
      if (caption && activeTab.dataset.caption) caption.textContent = activeTab.dataset.caption;

      mobilePanels.forEach((panel) => {
        panel.hidden = panel.dataset.zvFlowMobilePanel !== mode;
      });

      if (focus) activeTab.focus();
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => setMode(index));
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();

        let nextIndex = index;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = index - 1;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = index + 1;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;

        setMode(nextIndex, true);
      });
    });

    setMode(currentIndex);
  }

  function initDayCycle(root) {
    if (!root || root.dataset.zvReady) return;
    root.dataset.zvReady = 'true';

    const plot = root.querySelector('[data-zv-cycle-plot]');
    const buttons = [...root.querySelectorAll('[data-zv-cycle-time-button]')];
    const activeLine = root.querySelector('[data-zv-cycle-line]');
    const tooltip = root.querySelector('[data-zv-cycle-tooltip]');
    const tooltipTime = root.querySelector('[data-zv-cycle-tooltip-time]');
    const tooltipLabel = root.querySelector('[data-zv-cycle-tooltip-label]');
    const batteryFill = root.querySelector('[data-zv-cycle-fill]');
    const batteryGlow = root.querySelector('[data-zv-cycle-glow]');
    const moment = root.querySelector('[data-zv-cycle-moment]');
    const time = root.querySelector('[data-zv-cycle-time]');
    const chartMoment = root.querySelector('[data-zv-cycle-chart-moment]');
    const chartTime = root.querySelector('[data-zv-cycle-chart-time]');
    const percent = root.querySelector('[data-zv-cycle-percent]');
    const chargeLabel = root.querySelector('[data-zv-cycle-charge-label]');
    const description = root.querySelector('[data-zv-cycle-description]');
    const solar = root.querySelector('[data-zv-cycle-solar]');
    const action = root.querySelector('[data-zv-cycle-action]');
    const solarInline = root.querySelector('[data-zv-cycle-solar-inline]');
    const actionInline = root.querySelector('[data-zv-cycle-action-inline]');
    const mobileFill = root.querySelector('[data-zv-cycle-mobile-fill]');
    const mobilePercent = root.querySelector('[data-zv-cycle-mobile-percent]');

    if (!buttons.length) return;

    const states = buttons.map((button, index) => ({
      index,
      button,
      x: Number(button.dataset.x || 0),
      y: Number(button.dataset.y || 250),
      time: button.dataset.time || '',
      moment: button.dataset.moment || '',
      battery: Number(button.dataset.fill || 0),
      label: button.dataset.label || '',
      description: button.dataset.description || '',
      solar: button.dataset.solar || '',
      action: button.dataset.action || ''
    }));

    let currentIndex = Math.max(0, buttons.findIndex((button) => button.getAttribute('aria-selected') === 'true'));
    let frame = null;

    const interpolate = (startValue, endValue, progress) => startValue + ((endValue - startValue) * progress);

    const getInterpolatedState = (x) => {
      const clampedX = Math.max(states[0].x, Math.min(states[states.length - 1].x, x));
      let previous = states[0];
      let next = states[states.length - 1];

      for (let index = 0; index < states.length - 1; index += 1) {
        if (clampedX >= states[index].x && clampedX <= states[index + 1].x) {
          previous = states[index];
          next = states[index + 1];
          break;
        }
      }

      const range = Math.max(1, next.x - previous.x);
      const progress = Math.max(0, Math.min(1, (clampedX - previous.x) / range));
      const nearest = progress < 0.5 ? previous : next;

      return {
        ...nearest,
        x: clampedX,
        y: interpolate(previous.y, next.y, progress),
        battery: Math.round(interpolate(previous.battery, next.battery, progress))
      };
    };

    const updateVisual = (state, focusButton = false) => {
      if (!state) return;

      currentIndex = state.index;
      const svgX = state.x * 10;
      const batteryLevel = Math.max(8, Math.min(100, state.battery));
      const isFull = state.battery >= 100;

      buttons.forEach((button, index) => {
        const active = index === state.index;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', active ? 'true' : 'false');
        button.tabIndex = active ? 0 : -1;
      });

      if (activeLine) {
        activeLine.setAttribute('x1', svgX);
        activeLine.setAttribute('x2', svgX);
      }

      if (tooltip) tooltip.style.left = `${Math.max(6, Math.min(94, state.x))}%`;
      if (tooltipTime) tooltipTime.textContent = state.time;
      if (tooltipLabel) tooltipLabel.textContent = state.label;

      if (batteryFill) {
        batteryFill.style.height = `${batteryLevel}%`;
        batteryFill.classList.toggle('is-full', isFull);
      }
      if (mobileFill) mobileFill.style.height = `${batteryLevel}%`;
      if (batteryGlow) batteryGlow.classList.toggle('is-full', isFull);

      if (moment) moment.textContent = state.moment;
      if (time) time.textContent = state.time;
      if (chartMoment) chartMoment.textContent = state.moment;
      if (chartTime) chartTime.textContent = state.time;
      if (percent) percent.textContent = `${state.battery}%`;
      if (mobilePercent) mobilePercent.textContent = `${state.battery}%`;
      if (chargeLabel) chargeLabel.textContent = state.label;
      if (description) description.textContent = state.description;
      if (solar) solar.textContent = state.solar;
      if (action) action.textContent = state.action;
      if (solarInline) solarInline.textContent = state.solar;
      if (actionInline) actionInline.textContent = state.action;

      if (focusButton && state.button) state.button.focus();
    };

    const activateIndex = (index, focusButton = false) => {
      const normalizedIndex = (index + states.length) % states.length;
      updateVisual(states[normalizedIndex], focusButton);
    };

    const updateFromClientX = (clientX) => {
      if (!plot) return;
      const rect = plot.getBoundingClientRect();
      if (!rect.width) return;
      const x = ((clientX - rect.left) / rect.width) * 100;
      updateVisual(getInterpolatedState(x));
    };

    if (plot) {
      plot.addEventListener('pointermove', (event) => {
        if (frame) cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => updateFromClientX(event.clientX));
      });

      plot.addEventListener('click', (event) => updateFromClientX(event.clientX));

      plot.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();

        if (event.key === 'Home') activateIndex(0);
        if (event.key === 'End') activateIndex(states.length - 1);
        if (event.key === 'ArrowLeft') activateIndex(currentIndex - 1);
        if (event.key === 'ArrowRight') activateIndex(currentIndex + 1);
      });
    }

    buttons.forEach((button, index) => {
      button.addEventListener('click', () => activateIndex(index));
      button.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();

        let nextIndex = index;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = index - 1;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = index + 1;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = states.length - 1;

        activateIndex(nextIndex, true);
      });
    });

    activateIndex(currentIndex);
  }

  function initModular(root) {
    if (!root || root.dataset.zvReady) return;
    root.dataset.zvReady = 'true';

    const demo = root.querySelector('[data-zv-stack-demo]');
    const options = [...root.querySelectorAll('[data-zv-module-option]')];
    const capacity = root.querySelector('[data-zv-capacity]');
    const title = root.querySelector('[data-zv-capacity-title]');
    const description = root.querySelector('[data-zv-capacity-description]');
    const images = [...root.querySelectorAll('[data-zv-module-image]')];
    const revealLight = root.querySelector('[data-zv-module-reveal-light]');
    let activeImage = images.find((image) => image.classList.contains('is-active')) || images[0] || null;
    let activeModules = Number(activeImage?.dataset.zvModuleImage || demo?.dataset.modules || 1);
    let imageAnimation = null;
    let lightAnimation = null;
    const moduleTransitionDuration = 1080;

    const loadImage = (image) => {
      if (!image || image.getAttribute('src') || !image.dataset.src) return;
      image.src = image.dataset.src;
      delete image.dataset.src;
    };

    const loadModuleImage = (modules) => {
      loadImage(images.find((image) => image.dataset.zvModuleImage === modules));
    };

    const settleImages = (visibleImage) => {
      images.forEach((image) => {
        const isVisible = image === visibleImage;
        image.classList.toggle('is-active', isVisible);
        image.classList.remove('is-zv-underlay', 'is-zv-revealing');
        image.style.removeProperty('clip-path');
      });

      if (revealLight) {
        revealLight.style.removeProperty('opacity');
        revealLight.style.removeProperty('top');
      }
    };

    const cancelAnimations = () => {
      if (imageAnimation) {
        imageAnimation.cancel();
        imageAnimation = null;
      }
      if (lightAnimation) {
        lightAnimation.cancel();
        lightAnimation = null;
      }
      settleImages(activeImage);
    };

    const animateImageChange = (nextImage, nextModules) => {
      if (!nextImage) return;

      const previousImage = activeImage;
      const previousModules = activeModules;
      const increasing = nextModules > previousModules;

      cancelAnimations();
      activeImage = nextImage;
      activeModules = nextModules;

      if (
        !previousImage ||
        previousImage === nextImage ||
        reducedMotion.matches ||
        typeof nextImage.animate !== 'function'
      ) {
        settleImages(nextImage);
        return;
      }

      previousImage.classList.add('is-active', 'is-zv-underlay');
      nextImage.classList.add('is-active', 'is-zv-revealing');

      const startClip = increasing
        ? 'inset(0 0 100% 0)'
        : 'inset(100% 0 0 0)';

      imageAnimation = nextImage.animate(
        [
          { clipPath: startClip },
          { clipPath: 'inset(0 0 0 0)' }
        ],
        {
          duration: moduleTransitionDuration,
          easing: 'cubic-bezier(.22, .74, .2, 1)',
          fill: 'both'
        }
      );

      if (revealLight && typeof revealLight.animate === 'function') {
        lightAnimation = revealLight.animate(
          [
            { top: increasing ? '2%' : '98%', opacity: 0 },
            { opacity: 1, offset: 0.12 },
            { opacity: 1, offset: 0.82 },
            { top: increasing ? '98%' : '2%', opacity: 0 }
          ],
          {
            duration: moduleTransitionDuration,
            easing: 'cubic-bezier(.22, .74, .2, 1)',
            fill: 'both'
          }
        );
      }

      imageAnimation.finished
        .then(() => settleImages(nextImage))
        .catch(() => {});
    };

    const activate = (option) => {
      if (!option) return;
      const modules = option.dataset.modules || '1';
      const nextModules = Number(modules);
      const nextImage = images.find((image) => image.dataset.zvModuleImage === modules);

      loadModuleImage(modules);
      options.forEach((item) => item.setAttribute('aria-pressed', item.dataset.modules === modules ? 'true' : 'false'));
      if (demo) demo.dataset.modules = modules;
      if (capacity) capacity.textContent = option.dataset.capacity || '4 kWh';
      if (title) title.textContent = option.dataset.title || '';
      if (description) description.textContent = option.dataset.description || '';

      animateImageChange(nextImage, nextModules);
    };

    options.forEach((option) => {
      option.addEventListener('pointerenter', () => loadModuleImage(option.dataset.modules), { once: true });
      option.addEventListener('focus', () => loadModuleImage(option.dataset.modules), { once: true });
      option.addEventListener('click', () => activate(option));
    });

    const initial = options.find((option) => option.getAttribute('aria-pressed') === 'true') || options[0];
    if (initial) {
      const initialModules = initial.dataset.modules || '1';
      activeModules = Number(initialModules);
      activeImage = images.find((image) => image.dataset.zvModuleImage === initialModules) || activeImage;
      loadModuleImage(initialModules);
      settleImages(activeImage);
      if (capacity) capacity.textContent = initial.dataset.capacity || '4 kWh';
      if (title) title.textContent = initial.dataset.title || '';
      if (description) description.textContent = initial.dataset.description || '';
      if (demo) demo.dataset.modules = initialModules;
    }

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries, activeObserver) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        images.forEach(loadImage);
        activeObserver.disconnect();
      }, { rootMargin: '300px 0px' });
      observer.observe(root);
    }
  }

  function openFaqItem(item, content) {
    if (!content) return;

    if (reducedMotion.matches) {
      item.open = true;
      content.style.height = 'auto';
      return;
    }

    item.open = true;
    content.style.height = '0px';
    content.getBoundingClientRect();
    content.style.height = `${content.scrollHeight}px`;

    const onEnd = (event) => {
      if (event.propertyName !== 'height') return;
      content.style.height = 'auto';
      content.removeEventListener('transitionend', onEnd);
    };

    content.addEventListener('transitionend', onEnd);
  }

  function closeFaqItem(item, content) {
    if (!content) return;

    if (reducedMotion.matches) {
      item.open = false;
      content.style.height = '0px';
      return;
    }

    content.style.height = `${content.scrollHeight}px`;
    content.getBoundingClientRect();
    content.style.height = '0px';

    const onEnd = (event) => {
      if (event.propertyName !== 'height') return;
      item.open = false;
      content.removeEventListener('transitionend', onEnd);
    };

    content.addEventListener('transitionend', onEnd);
  }

  function initFaq(scope = document) {
    scope.querySelectorAll('[data-zv-faq]').forEach((faq) => {
      if (faq.dataset.zvReady) return;
      faq.dataset.zvReady = 'true';

      const items = [...faq.querySelectorAll('details')];

      items.forEach((item) => {
        const summary = item.querySelector('summary');
        const content = item.querySelector('[data-zv-faq-content]');
        if (!summary || !content) return;

        content.style.height = item.open ? 'auto' : '0px';

        summary.addEventListener('click', (event) => {
          event.preventDefault();

          if (item.open) {
            closeFaqItem(item, content);
            return;
          }

          items.forEach((other) => {
            if (other === item || !other.open) return;
            closeFaqItem(other, other.querySelector('[data-zv-faq-content]'));
          });

          openFaqItem(item, content);
        });
      });
    });
  }

  function initChargeRing(ring) {
    if (!ring || ring.dataset.zvChargeReady) return;
    ring.dataset.zvChargeReady = 'true';

    const value = ring.querySelector('[data-zv-charge-value]');
    const cycleLabel = ring.querySelector('[data-zv-mode-ring-label]');
    const clamp = (number, min, max) => Math.min(max, Math.max(min, number));
    const easeOutCubic = (progress) => 1 - Math.pow(1 - progress, 3);
    const easeInOutSine = (progress) => -(Math.cos(Math.PI * progress) - 1) / 2;
    const modeProfiles = {
      'self-use': { min: 20, max: 100, segmentDuration: 42000, direction: 'cycle' },
      'dynamic-energy': { target: 90, duration: 4800, direction: 'up' },
      'fast-charge': { target: 100, duration: 2800, direction: 'up' },
      'max-discharge': { target: 10, duration: 3000, direction: 'down' },
      'custom-schedule': { target: 62, duration: 4200, direction: 'target' }
    };

    let currentPercentage = clamp(Number(value?.textContent || 78), 0, 100);
    let activeMode = ring.closest('[data-zv-app-device]')?.dataset.activeMode || 'self-use';
    let transitionStart = null;
    let transitionFrom = currentPercentage;
    let targetPercentage = currentPercentage;
    let transitionDuration = 0;
    let selfUseDirection = 1;
    let frame = 0;
    let active = false;
    let inView = false;

    const render = (percentage) => {
      const rounded = clamp(Math.round(percentage), 0, 100);
      currentPercentage = rounded;
      ring.style.setProperty('--zv-charge', `${rounded}%`);
      if (value) value.textContent = String(rounded);
    };

    const requestFrame = () => {
      if (active && !document.hidden && !frame && !reducedMotion.matches) {
        frame = window.requestAnimationFrame(animate);
      }
    };

    const animate = (timestamp) => {
      frame = 0;
      if (!active || document.hidden || !ring.isConnected || reducedMotion.matches) return;
      if (transitionStart === null) transitionStart = timestamp;

      const elapsed = timestamp - transitionStart;
      const progress = transitionDuration > 0
        ? clamp(elapsed / transitionDuration, 0, 1)
        : 1;
      const easedProgress = activeMode === 'self-use'
        ? easeInOutSine(progress)
        : easeOutCubic(progress);
      const percentage = transitionFrom
        + ((targetPercentage - transitionFrom) * easedProgress);

      render(percentage);
      if (progress < 1) {
        requestFrame();
      } else if (activeMode === 'self-use') {
        queueSelfUseSegment(selfUseDirection * -1);
        requestFrame();
      }
    };

    const queueSelfUseSegment = (direction = 1) => {
      const profile = modeProfiles['self-use'];
      selfUseDirection = direction >= 0 ? 1 : -1;
      transitionFrom = currentPercentage;
      targetPercentage = selfUseDirection > 0 ? profile.max : profile.min;
      if (cycleLabel) {
        cycleLabel.textContent = selfUseDirection > 0
          ? 'Zonnestroom wordt opgeslagen'
          : 'Opgeslagen stroom wordt gebruikt';
      }

      const fullRange = profile.max - profile.min;
      const remainingRange = Math.abs(targetPercentage - currentPercentage);
      transitionDuration = Math.max(
        3200,
        profile.segmentDuration * (remainingRange / fullRange)
      );
      transitionStart = null;
    };

    const setMode = (mode) => {
      activeMode = mode || 'self-use';
      const profile = modeProfiles[activeMode] || modeProfiles['self-use'];

      if (activeMode === 'self-use') {
        const direction = currentPercentage >= profile.max - .5 ? -1 : 1;
        queueSelfUseSegment(direction);

        if (!reducedMotion.matches) requestFrame();
        return;
      }

      const requestedTarget = clamp(profile.target, 0, 100);

      transitionFrom = currentPercentage;
      targetPercentage = profile.direction === 'up'
        ? Math.max(currentPercentage, requestedTarget)
        : profile.direction === 'down'
          ? Math.min(currentPercentage, requestedTarget)
          : requestedTarget;
      transitionDuration = profile.duration;
      transitionStart = null;

      if (reducedMotion.matches) {
        render(targetPercentage);
        return;
      }

      if (Math.abs(targetPercentage - currentPercentage) < .5) {
        render(targetPercentage);
        return;
      }

      requestFrame();
    };

    const setActive = (nextActive) => {
      const becomingActive = nextActive && !active;
      active = nextActive;

      if (becomingActive && transitionStart !== null) {
        transitionFrom = currentPercentage;
        transitionStart = null;

        if (activeMode === 'self-use') {
          const profile = modeProfiles['self-use'];
          const fullRange = profile.max - profile.min;
          const remainingRange = Math.abs(targetPercentage - currentPercentage);
          transitionDuration = Math.max(
            3200,
            profile.segmentDuration * (remainingRange / fullRange)
          );
        }
      }

      if (active) requestFrame();
      if (!active && frame) {
        window.cancelAnimationFrame(frame);
        frame = 0;
      }
    };

    ring.zvSetMode = setMode;
    setMode(activeMode);

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        inView = entries.some((entry) => entry.isIntersecting);
        setActive(inView && !document.hidden);
      }, { rootMargin: '120px 0px' });
      observer.observe(ring);
    } else {
      inView = true;
      setActive(!document.hidden);
    }

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && inView && Math.abs(targetPercentage - currentPercentage) >= .5) {
        transitionFrom = currentPercentage;
        transitionStart = null;
      }
      setActive(inView && !document.hidden);
    }, { passive: true });
  }


  function initAppModeSelector(device) {
    if (!device || device.dataset.zvModeReady) return;
    device.dataset.zvModeReady = 'true';

    const openButton = device.querySelector('[data-zv-mode-open]');
    const closeButton = device.querySelector('[data-zv-mode-close]');
    const screen = device.querySelector('[data-zv-mode-screen]');
    const options = [...device.querySelectorAll('[data-zv-mode-option]')];
    const activeLabel = device.querySelector('[data-zv-active-mode]');
    const ringLabel = device.querySelector('[data-zv-mode-ring-label]');
    const solarValue = device.querySelector('[data-zv-mode-solar-value]');
    const solarLabel = device.querySelector('[data-zv-mode-solar-label]');
    const homeValue = device.querySelector('[data-zv-mode-home-value]');
    const homeLabel = device.querySelector('[data-zv-mode-home-label]');
    const dynamicValue = device.querySelector('[data-zv-mode-dynamic-value]');
    const dynamicLabel = device.querySelector('[data-zv-mode-dynamic-label]');
    const chargeRing = device.querySelector('[data-zv-charge-ring]');

    if (!openButton || !screen || !options.length) return;

    let modeTransitionTimer = 0;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const modeTransitionDuration = prefersReducedMotion ? 0 : 400;

    const closeScreen = (restoreFocus = true) => {
      window.clearTimeout(modeTransitionTimer);
      screen.classList.remove('is-open');
      device.classList.remove('is-mode-open');
      openButton.setAttribute('aria-expanded', 'false');

      modeTransitionTimer = window.setTimeout(() => {
        screen.hidden = true;
        if (restoreFocus) openButton.focus();
      }, modeTransitionDuration);
    };

    const openScreen = () => {
      window.clearTimeout(modeTransitionTimer);
      screen.hidden = false;
      openButton.setAttribute('aria-expanded', 'true');
      const selected = options.find((option) => option.getAttribute('aria-selected') === 'true') || options[0];

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          device.classList.add('is-mode-open');
          screen.classList.add('is-open');
          selected.focus({ preventScroll: true });
        });
      });
    };

    const setMode = (option, closeAfter = true) => {
      if (!option) return;

      const mode = option.dataset.zvModeOption || 'self-use';
      const label = option.dataset.modeLabel || option.querySelector('strong')?.textContent || '';
      const modeRingLabel = option.dataset.modeRingLabel || '';
      const modeSolarValue = option.dataset.modeSolarValue || '';
      const modeSolarLabel = option.dataset.modeSolarLabel || '';
      const modeHomeValue = option.dataset.modeHomeValue || '';
      const modeHomeLabel = option.dataset.modeHomeLabel || '';
      const modeDynamicValue = option.dataset.modeDynamicValue || '€ 0,02';
      const modeDynamicLabel = option.dataset.modeDynamicLabel || 'Dynamisch tarief';
      const modeShowDynamic = option.dataset.modeShowDynamic || 'false';

      device.dataset.activeMode = mode;
      device.dataset.dynamicVisible = modeShowDynamic;
      if (chargeRing && typeof chargeRing.zvSetMode === 'function') chargeRing.zvSetMode(mode);
      if (activeLabel) activeLabel.textContent = label;
      if (ringLabel) ringLabel.textContent = modeRingLabel;
      if (solarValue) solarValue.textContent = modeSolarValue;
      if (solarLabel) solarLabel.textContent = modeSolarLabel;
      if (homeValue) homeValue.textContent = modeHomeValue;
      if (homeLabel) homeLabel.textContent = modeHomeLabel;
      if (dynamicValue) dynamicValue.textContent = modeDynamicValue;
      if (dynamicLabel) dynamicLabel.textContent = modeDynamicLabel;

      options.forEach((item) => {
        const active = item === option;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      if (closeAfter) closeScreen(false);
    };

    openButton.addEventListener('click', openScreen);
    if (closeButton) closeButton.addEventListener('click', () => closeScreen());

    options.forEach((option) => {
      option.addEventListener('click', () => {
        setMode(option, false);
        closeScreen(true);
      });
    });

    device.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || screen.hidden) return;
      event.preventDefault();
      closeScreen();
    });

    const initial = options.find((option) => option.getAttribute('aria-selected') === 'true') || options[0];
    setMode(initial, false);
  }


  function initHeaderOverlay() {
    if (window.__zvHeaderOverlayReady && typeof window.__zvUpdateHeaderOverlay === 'function') {
      window.__zvUpdateHeaderOverlay();
      return;
    }

    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
    let frame = 0;
    let lastProgress = -1;
    let lastHeaderHeight = -1;

    const update = () => {
      frame = 0;
      const header = document.querySelector('.shopify-section-header');
      const hero = document.querySelector('.shopify-section.zv-home-section--hero .zv-hero, .zv-hero');
      const headerHeight = header ? Math.max(0, Math.round(header.getBoundingClientRect().height)) : 72;

      if (headerHeight !== lastHeaderHeight) {
        lastHeaderHeight = headerHeight;
        document.documentElement.style.setProperty('--zv-header-height', `${headerHeight}px`);
      }

      let progress = 0;
      if (hero) {
        const heroBottom = hero.getBoundingClientRect().bottom;
        const fadeDistance = Math.max(140, headerHeight * 2.1);
        progress = clamp(1 - ((heroBottom - headerHeight) / fadeDistance), 0, 1);
      } else {
        progress = window.scrollY > 18 ? 1 : 0;
      }

      if (Math.abs(progress - lastProgress) < 0.004) return;
      lastProgress = progress;

      const dark = [5, 12, 7, 0.10];
      const light = [255, 255, 255, 0.98];
      const values = dark.map((value, index) => value + ((light[index] - value) * progress));
      const rgb = `${Math.round(values[0])} ${Math.round(values[1])} ${Math.round(values[2])}`;
      const alpha = values[3].toFixed(3);

      document.body.style.setProperty('--zv-header-rgb', rgb);
      document.body.style.setProperty('--zv-header-alpha', alpha);
      document.body.style.setProperty('--zv-header-bg', `rgba(${Math.round(values[0])}, ${Math.round(values[1])}, ${Math.round(values[2])}, ${alpha})`);
      document.body.classList.toggle('zv-header-at-top', progress < 0.5);
      document.body.classList.toggle('zv-header-on-light', progress >= 0.58);
    };

    const requestUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    window.__zvUpdateHeaderOverlay = requestUpdate;
    requestUpdate();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    window.__zvHeaderOverlayReady = true;
  }

  function initCountdowns(scope = document) {
    scope.querySelectorAll(SELECTORS.countdown).forEach((countdown) => {
      if (!countdown || countdown.dataset.zvCountdownReady) return;

      const value = countdown.querySelector('[data-zv-countdown-value]');
      const label = countdown.querySelector('[data-zv-countdown-label]');
      const target = new Date(countdown.dataset.zvCountdownTarget || '2027-01-01T00:00:00+01:00');

      if (!value || Number.isNaN(target.getTime())) return;
      countdown.dataset.zvCountdownReady = 'true';

      const update = () => {
        if (!countdown.isConnected) {
          window.clearInterval(countdown.zvCountdownTimer);
          return;
        }

        const milliseconds = Math.max(0, target.getTime() - Date.now());
        const days = Math.ceil(milliseconds / 86400000);
        const singular = countdown.dataset.zvCountdownLabelSingular || 'dag tot 2027';
        const plural = countdown.dataset.zvCountdownLabelPlural || 'dagen tot 2027';
        const activeLabel = days === 1 ? singular : plural;

        value.textContent = String(days);
        if (label) label.textContent = activeLabel;
        countdown.setAttribute('aria-label', `${days} ${activeLabel}`);
      };

      update();
      countdown.zvCountdownTimer = window.setInterval(update, 60 * 60 * 1000);
    });
  }

  function initCounters(scope = document) {
    const counters = [...scope.querySelectorAll(SELECTORS.counters)].filter((item) => !item.dataset.zvCounterReady);
    if (!counters.length) return;

    const animateCounter = (item) => {
      item.dataset.zvCounterReady = 'true';
      const raw = item.dataset.zvCounter || item.textContent || '';
      const digits = parseInt(String(raw).replace(/[^0-9]/g, ''), 10);
      if (!digits) return;
      const suffix = String(raw).trim().endsWith('+') ? '+' : '';
      const duration = 1400;
      let start;

      const tick = (timestamp) => {
        if (start === undefined) start = timestamp;
        const progress = Math.min((timestamp - start) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        const current = Math.round(digits * eased);
        item.textContent = `${current.toLocaleString('nl-NL')}${suffix}`;
        if (progress < 1) window.requestAnimationFrame(tick);
      };

      window.requestAnimationFrame(tick);
    };

    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      counters.forEach((item) => {
        item.textContent = item.dataset.zvCounterLabel || item.dataset.zvCounter || item.textContent;
        item.dataset.zvCounterReady = 'true';
      });
      return;
    }

    const observer = new IntersectionObserver((entries, activeObserver) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        animateCounter(entry.target);
        activeObserver.unobserve(entry.target);
      });
    }, { threshold: 0.45 });

    counters.forEach((item) => observer.observe(item));
  }

  function initReviewSliders(scope = document) {
    scope.querySelectorAll(SELECTORS.reviews).forEach((root) => {
      if (!root || root.dataset.zvReviewsReady === 'true') return;

      const track = root.querySelector('[data-zv-reviews-track]');
      const cards = [...root.querySelectorAll('.zv-review')];
      const dots = [...root.querySelectorAll('[data-zv-review-dot]')];
      if (!track || cards.length < 2 || dots.length !== cards.length) return;

      root.dataset.zvReviewsReady = 'true';
      let frame = 0;

      const setActiveDot = () => {
        frame = 0;
        const trackLeft = track.getBoundingClientRect().left;
        let activeIndex = 0;
        let nearest = Number.POSITIVE_INFINITY;

        cards.forEach((card, index) => {
          const distance = Math.abs(card.getBoundingClientRect().left - trackLeft);
          if (distance < nearest) {
            nearest = distance;
            activeIndex = index;
          }
        });

        dots.forEach((dot, index) => {
          const active = index === activeIndex;
          dot.classList.toggle('is-active', active);
          if (active) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      };

      const requestDotUpdate = () => {
        if (!frame) frame = window.requestAnimationFrame(setActiveDot);
      };

      dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          const card = cards[index];
          if (!card) return;
          const left = card.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;
          track.scrollTo({ left, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
        });
      });

      track.addEventListener('scroll', requestDotUpdate, { passive: true });
      window.addEventListener('resize', requestDotUpdate, { passive: true });
      requestDotUpdate();
    });
  }


  function initBlogSliders(scope = document) {
    scope.querySelectorAll(SELECTORS.blogSliders).forEach((root) => {
      if (!root || root.dataset.zvBlogReady === 'true') return;

      const track = root.querySelector('[data-zv-blog-track]');
      const cards = [...root.querySelectorAll('.zv-home-blog__card')];
      const dots = [...root.querySelectorAll('[data-zv-blog-dot]')];
      if (!track || cards.length < 2 || dots.length !== cards.length) return;

      root.dataset.zvBlogReady = 'true';
      let frame = 0;

      const setActiveDot = () => {
        frame = 0;
        const trackLeft = track.getBoundingClientRect().left;
        let activeIndex = 0;
        let nearest = Number.POSITIVE_INFINITY;

        cards.forEach((card, index) => {
          const distance = Math.abs(card.getBoundingClientRect().left - trackLeft);
          if (distance < nearest) {
            nearest = distance;
            activeIndex = index;
          }
        });

        dots.forEach((dot, index) => {
          const active = index === activeIndex;
          dot.classList.toggle('is-active', active);
          if (active) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
      };

      const requestDotUpdate = () => {
        if (!frame) frame = window.requestAnimationFrame(setActiveDot);
      };

      dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          const card = cards[index];
          if (!card) return;
          const left = card.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;
          track.scrollTo({ left, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
        });
      });

      track.addEventListener('scroll', requestDotUpdate, { passive: true });
      window.addEventListener('resize', requestDotUpdate, { passive: true });
      requestDotUpdate();
    });
  }

  function initAnimationVisibility(scope = document) {
    const roots = [...scope.querySelectorAll('.zv-flow-section, [data-zv-daycycle]')]
      .filter((root) => !root.dataset.zvAnimationObserverReady);

    if (!roots.length) return;

    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      roots.forEach((root) => {
        root.dataset.zvAnimationObserverReady = 'true';
        root.classList.add('is-zv-animation-active');
      });
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        entry.target.classList.toggle('is-zv-animation-active', entry.isIntersecting);
      });
    }, { rootMargin: '240px 0px', threshold: 0 });

    roots.forEach((root) => {
      root.dataset.zvAnimationObserverReady = 'true';
      observer.observe(root);
    });
  }

  function initSmoothAnchors(scope = document) {
    scope.querySelectorAll('.zv-home a[href^="#"]:not([href="#"])').forEach((anchor) => {
      if (anchor.dataset.zvAnchorReady) return;
      anchor.dataset.zvAnchorReady = 'true';

      anchor.addEventListener('click', (event) => {
        const selector = anchor.getAttribute('href');
        const target = selector ? document.querySelector(selector) : null;

        if (!target || selector === '#zv-advies-popup') return;

        event.preventDefault();
        const scrollTarget = selector === '#zv-werking'
          ? target.querySelector('.zv-flow-panel') || target
          : target;
        const header = document.querySelector('.shopify-section-group-header-group, .section-header, .shopify-section.section-header');
        const headerHeight = header ? Math.max(0, header.getBoundingClientRect().height) : 72;
        const destination = Math.max(0, scrollTarget.getBoundingClientRect().top + window.scrollY - headerHeight - 18);

        window.scrollTo({
          top: destination,
          behavior: reducedMotion.matches ? 'auto' : 'smooth'
        });
      });
    });
  }

  function init(scope = document) {
    document.body.classList.add('zv-homepage-active');
    initHeaderOverlay();
    initReveals(scope);
    scope.querySelectorAll(SELECTORS.flow).forEach(initFlow);
    scope.querySelectorAll(SELECTORS.day).forEach(initDayCycle);
    scope.querySelectorAll(SELECTORS.modular).forEach(initModular);
    scope.querySelectorAll(SELECTORS.chargeRing).forEach(initChargeRing);
    scope.querySelectorAll(SELECTORS.appDevice).forEach(initAppModeSelector);
    initCountdowns(scope);
    initCounters(scope);
    initFaq(scope);
    initReviewSliders(scope);
    initBlogSliders(scope);
    initAnimationVisibility(scope);
    initSmoothAnchors(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
